/**
 * Typed HTTP client for the Gridiron Tracker API gateway.
 *
 * One instance is created in `main.tsx` and shared through React context. It
 * owns four cross-cutting concerns so no page has to:
 *   1. base URL — `VITE_API_BASE_URL`, relative by default so the bundle holds
 *      no infrastructure hostname;
 *   2. auth context — `Authorization: Bearer …` plus `X-Tenant-ID` for the
 *      fanatic persona, whose tenant is not fixed by the token;
 *   3. errors — RFC 7807 → `ApiError` (see api-error.ts), 401 → `onUnauthorized`
 *      (except a credential check — MFA code — failing with a domain code);
 *   4. idempotency — every mutation carries an `Idempotency-Key`.
 */
import {
  ApiError,
  isSessionError,
  SESSION_EXPIRED_MESSAGE,
  toApiError,
  toNetworkError,
} from './api-error';
import type {
  AccountEmailChanged,
  BillingPlanCatalog,
  BillingPortalSession,
  BillingSubscriptionView,
  CheckoutSession,
  CheckoutSessionInput,
  CloseEventInput,
  CohortValue,
  CohortValuePage,
  CohortValueQuery,
  CreateCohortValueInput,
  CreatedTeamUser,
  CreateTeamUserInput,
  EventInput,
  EventPage,
  EventPatch,
  EventQuery,
  FanaticAccessMode,
  FanaticGrant,
  FanaticGrantPage,
  FanaticGrantQuery,
  FanaticInviteAcceptance,
  FanaticInvitePreview,
  FanaticRealmInfo,
  FaqEntry,
  FaqPage,
  FaqQuery,
  GameSummaryFeed,
  HighlightCurrentDown,
  HighlightFeed,
  HighlightKeyStat,
  HighlightPieChart,
  HighlightPieSlice,
  HighlightSide,
  DownloadedFile,
  ExportTemplate,
  ExportTemplateInput,
  ExportTemplatePatch,
  TableExportRequest,
  HudlCommitInput,
  HudlCommitResult,
  HudlExportFieldCatalog,
  HudlExportFileQuery,
  HudlExportTemplate,
  HudlExportTemplateInput,
  HudlExportTemplatePage,
  HudlExportTemplatePatch,
  HudlImportPreview,
  HudlImportRecordPage,
  HudlReconcileInput,
  HudlReconcileResult,
  LayoutScope,
  InvoicePage,
  InvoiceQuery,
  KeyStatFormat,
  LinkedIdentityList,
  MfaEnrollment,
  MfaSettings,
  MfaSettingsPatch,
  MfaSettingsUpdate,
  MfaVerification,
  PasswordResetRequested,
  SessionPage,
  SessionResource,
  ParticipationPage,
  ParticipationUpdateInput,
  PlaybookCatalog,
  PlaybookLibrary,
  StatCategory,
  StatOption,
  StatOptionInput,
  StatOptionList,
  StatOptionPatch,
  StatOptionRetired,
  StatSide,
  PlayPage,
  PlayPatch,
  PlayQuery,
  PlayVersionPage,
  PlayView,
  Player,
  PlayerInput,
  PlayerPage,
  PlayerPatch,
  PlayerQuery,
  PublicTeam,
  PublicTeamPage,
  PublicTeamQuery,
  ReopenEventInput,
  ResolveConflictInput,
  InsertPlayInput,
  RevokedFanaticGrant,
  SeasonRollupFeed,
  SeasonRollupQuery,
  SeatUsage,
  SentFanaticInvite,
  SignupOptions,
  SignupRequest,
  SignupResult,
  SlugAvailability,
  BoardLayoutInput,
  BoardLayoutPage,
  BoardLayoutRecord,
  NamedLayoutInput,
  NamedLayoutPatch,
  EnumCatalogPage,
  EnumCatalogValue,
  StatFieldCapacity,
  StatFieldDefinition,
  StatFieldEnumOption,
  StatFieldInput,
  StatFieldPatch,
  StatSchemaPage,
  StatSchemaQuery,
  StatTrackerFeed,
  StatUnit,
  StatUnitFilter,
  SubscriptionPatch,
  SupportRecipientStatus,
  SupportRequestCreated,
  FanSignInOptions,
  FanaticSignupRequest,
  SupportRequestInput,
  TeamEvent,
  TeamFanaticGrantPage,
  TeamFanaticGrantQuery,
  TeamLookup,
  TeamUser,
  TeamUserPage,
  TeamUserQuery,
  TenantSummary,
  TokenResponse,
  TooltipResolution,
  TooltipSource,
  EventVideoEmbed,
  EventVideoEmbedInput,
  EventVideoEmbedPage,
  EventVideoEmbedQuery,
  SuccessMeasureConfig,
  SuccessMeasureConfigView,
  VideoEmbedInfo,
  VideoProviderCatalog,
  VideoSettings,
  VideoSettingsPatch,
} from './api-types';
import { API_PATHS, TIER_LIMIT_ERROR_CODE, TIER_LIMIT_STATUS } from './constants';
import type { Persona } from './personas';

// The wire shapes live in `api-types.ts`; re-exported so callers keep importing
// the client and its contracts from one module.
export type {
  AccountEmailChanged,
  CloseEventInput,
  CohortValue,
  CohortValuePage,
  CohortValueQuery,
  CreateCohortValueInput,
  CreatedTeamUser,
  CreateTeamUserInput,
  EventInput,
  EventPage,
  EventPatch,
  EventQuery,
  FaqEntry,
  FaqPage,
  FaqQuery,
  GameSummaryFeed,
  HighlightCurrentDown,
  HighlightFeed,
  HighlightKeyStat,
  HighlightPieChart,
  HighlightPieSlice,
  HighlightSide,
  KeyStatFormat,
  MfaEnrollment,
  MfaSettings,
  MfaSettingsPatch,
  MfaSettingsUpdate,
  MfaVerification,
  PasswordResetRequested,
  SessionPage,
  SessionResource,
  ParticipationPage,
  ParticipationUpdateInput,
  PlaybookCatalog,
  PlaybookLibrary,
  StatCategory,
  StatOption,
  StatOptionInput,
  StatOptionList,
  StatOptionPatch,
  StatOptionRetired,
  StatSide,
  PlayPage,
  PlayPatch,
  PlayQuery,
  PlayVersionPage,
  PlayView,
  Player,
  PlayerInput,
  PlayerPage,
  PlayerPatch,
  PlayerQuery,
  ReopenEventInput,
  ResolveConflictInput,
  InsertPlayInput,
  SeasonRollupFeed,
  SeasonRollupQuery,
  SeatUsage,
  SignupOptions,
  SignupRequest,
  SignupResult,
  SlugAvailability,
  BoardLayoutInput,
  BoardLayoutPage,
  BoardLayoutRecord,
  NamedLayoutInput,
  NamedLayoutPatch,
  EnumCatalogPage,
  EnumCatalogValue,
  StatFieldCapacity,
  StatFieldDefinition,
  StatFieldEnumOption,
  StatFieldInput,
  StatFieldPatch,
  StatSchemaPage,
  StatSchemaQuery,
  StatTrackerFeed,
  StatUnit,
  StatUnitFilter,
  SupportRecipientStatus,
  SupportRequestCreated,
  SupportRequestInput,
  TeamEvent,
  TeamLookup,
  TeamUser,
  TeamUserPage,
  TeamUserQuery,
  TenantSummary,
  TokenResponse,
  TooltipResolution,
  TooltipSource,
  FanaticAccessMode,
  FanaticGrant,
  FanaticGrantPage,
  FanaticGrantQuery,
  FanaticInviteAcceptance,
  FanaticInvitePreview,
  FanaticRealmInfo,
  LinkedIdentityList,
  PublicTeam,
  PublicTeamPage,
  PublicTeamQuery,
  RevokedFanaticGrant,
  SentFanaticInvite,
  TeamFanaticGrantPage,
  TeamFanaticGrantQuery,
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Skip the Authorization header (public endpoints). */
  isPublic?: boolean;
  /**
   * Treat a 401 as a verdict on the REQUEST, not on the session. The auth
   * service answers a wrong authenticator code or password with 401
   * (`MFA_INVALID_CODE`); routing that through `onUnauthorized` would sign the
   * user out mid-flow — after which the retry has no bearer token and the
   * gateway's "This endpoint requires a bearer token." leaks into the UI.
   * `onUnauthorized` is still invoked when the 401 carries a credential code
   * (see `isSessionError`), because then the session really is gone.
   */
  isCredentialCheck?: boolean;
  /**
   * The calling screen renders the 402 `TIER_LIMIT_EXCEEDED` itself as an
   * INLINE plan-gate card (Team Settings → Video, Live Video), so the app-wide
   * `TierLimitBoundary` modal must not also open over it — a second, dimming
   * overlay on top of an inline notice is exactly the pattern the product is
   * moving away from. The error still propagates to the caller.
   */
  handlesTierLimit?: boolean;
  signal?: AbortSignal;
  /** Reuse a caller-supplied idempotency key for a retried mutation. */
  idempotencyKey?: string;
}

export interface AuthContextProvider {
  accessToken: string | null;
  /** Sent as `X-Tenant-ID` for personas whose token has no `tid` (fanatic). */
  tenantId: string | null;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Read at call time so a token refresh is picked up without re-creating the client. */
  getAuthContext: () => AuthContextProvider;
  onUnauthorized?: () => void;
  /**
   * Called for every 402 `TIER_LIMIT_EXCEEDED` (FR-BILLING-05) before the error
   * is thrown, so the app-wide upgrade CTA can appear no matter which screen
   * made the call. Callers still receive the rejection and stay in control of
   * their own inline messaging.
   */
  onTierLimit?: (error: ApiError) => void;
  fetchImpl?: typeof fetch;
  /** Injectable for tests. */
  newIdempotencyKey?: () => string;
}

const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) search.set(key, String(value));
  }
  const suffix = search.toString();
  return `${baseUrl}${path}${suffix === '' ? '' : `?${suffix}`}`;
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  /**
   * Authenticated GET whose body is NOT JSON (CSV / NDJSON exports). Used by the
   * Platform Admin audit export, which must go through the server so the export
   * itself is audit-logged.
   */
  downloadExport(path: string, query?: RequestOptions['query']): Promise<Blob>;
  getSignupOptions(signal?: AbortSignal): Promise<SignupOptions>;
  checkSlugAvailability(slug: string, signal?: AbortSignal): Promise<SlugAvailability>;
  lookupTeam(slug: string, signal?: AbortSignal): Promise<TeamLookup>;
  /**
   * Which sign-in methods the fan pages should offer. Public.
   *
   * `providers` may legitimately be empty — every consumer provider ships
   * disabled until its Keycloak broker is configured — so callers must render
   * the password form regardless and treat social buttons as additive.
   */
  getFanSignInOptions(signal?: AbortSignal): Promise<FanSignInOptions>;
  /** `POST /auth/v1/signup/fanatic` — self-service fan account. Public. */
  signUpFanatic(input: FanaticSignupRequest): Promise<TokenResponse>;
  createTeam(input: SignupRequest): Promise<SignupResult>;
  login(input: { tenantId: string; email: string; password: string }): Promise<TokenResponse>;
  refresh(refreshToken: string): Promise<TokenResponse>;
  revokeRefreshToken(refreshToken: string): Promise<void>;
  enrollMfa(): Promise<MfaEnrollment>;
  verifyMfa(code: string): Promise<MfaVerification>;
  /**
   * `POST /auth/v1/password/reset/request` — public. Always 202, whether or
   * not the account exists (anti-enumeration). `tenantId` is omitted for
   * Fanatic accounts: the server resolves those against the shared fan realm.
   */
  requestPasswordReset(input: {
    tenantId?: string;
    email: string;
  }): Promise<PasswordResetRequested>;
  /**
   * `PATCH /auth/v1/account` — Fanatic self-service email change.
   * `currentPassword` is required whenever the account has a password;
   * OAuth-only accounts omit it. 409 `EMAIL_IN_USE`, 401 `INVALID_CREDENTIALS`.
   */
  changeAccountEmail(input: {
    email: string;
    currentPassword?: string;
  }): Promise<AccountEmailChanged>;
  /**
   * `POST /auth/v1/password/reset/confirm` — public. 204 on success; 400
   * `INVALID_RESET_TOKEN` / `PASSWORD_*`, 410 `RESET_TOKEN_CONSUMED`.
   */
  confirmPasswordReset(input: { token: string; newPassword: string }): Promise<void>;
  /** `GET /auth/v1/sessions` — the caller's own sign-ins (FR-AUTH-04). */
  listSessions(
    query?: { status?: 'all' | 'active' | 'inactive'; limit?: number; offset?: number },
    signal?: AbortSignal
  ): Promise<SessionPage>;
  /** `DELETE /auth/v1/sessions/{session_id}` — revoke one device. Idempotent 204. */
  revokeSession(sessionId: string): Promise<void>;
  getTenant(tenantId: string): Promise<TenantSummary>;
  /** `GET /users/v1` — Team Admin user list (paginated, sorted, filtered). */
  listTeamUsers(query: TeamUserQuery, signal?: AbortSignal): Promise<TeamUserPage>;
  /** `POST /users/v1` — invite or directly create a member. */
  createTeamUser(input: CreateTeamUserInput): Promise<CreatedTeamUser>;
  /** `PATCH /users/v1/{id}` — persona change, rename, reinstate. */
  updateTeamUser(
    userId: string,
    patch: { persona?: Persona; display_name?: string | null; active?: boolean }
  ): Promise<TeamUser>;
  /** `DELETE /users/v1/{id}` — soft-delete the membership. */
  removeTeamUser(userId: string): Promise<void>;
  /** `GET /players/v1` — roster page (search, cohort filter, sort, paging). */
  listPlayers(query: PlayerQuery, signal?: AbortSignal): Promise<PlayerPage>;
  /** `POST /players/v1` — add a roster row (coaching staff + Team Admin). */
  createPlayer(input: PlayerInput): Promise<Player>;
  /** `PATCH /players/v1/{player_id}` — partial update. */
  updatePlayer(playerId: string, patch: PlayerPatch): Promise<Player>;
  /** `DELETE /players/v1/{player_id}` — soft delete (Team Admin only). */
  removePlayer(playerId: string): Promise<void>;
  /** `GET /cohort-values/v1` — the tenant's cohort catalog. */
  listCohortValues(query: CohortValueQuery, signal?: AbortSignal): Promise<CohortValuePage>;
  /** `POST /cohort-values/v1` — extend the catalog (Team Admin only). */
  createCohortValue(input: CreateCohortValueInput): Promise<CohortValue>;
  /** `GET /events/v1` — event page (season/type/status filters, sort, paging). */
  listEvents(query: EventQuery, signal?: AbortSignal): Promise<EventPage>;
  /** `GET /events/v1/{event_id}` */
  getEvent(eventId: string, signal?: AbortSignal): Promise<TeamEvent>;
  /** `POST /events/v1` — lands in `pending` (Team Admin + coaching staff). */
  createEvent(input: EventInput): Promise<TeamEvent>;
  /** `PATCH /events/v1/{event_id}` — metadata plus the `pending → active` start. */
  updateEvent(eventId: string, patch: EventPatch): Promise<TeamEvent>;
  /** `POST /events/v1/{event_id}/close` — `active → closed` with an audited reason. */
  closeEvent(eventId: string, input: CloseEventInput): Promise<TeamEvent>;
  /** `POST /events/v1/{event_id}/reopen` — Team Admin only, audited reason. */
  reopenEvent(eventId: string, input: ReopenEventInput): Promise<TeamEvent>;
  /** `DELETE /events/v1/{event_id}` — Team Admin only; soft delete, audit-logged. */
  deleteEvent(eventId: string): Promise<void>;
  /** `GET /events/v1/{event_id}/participation` — roster-driven checklist. */
  listParticipation(eventId: string, signal?: AbortSignal): Promise<ParticipationPage>;
  /** `PUT /events/v1/{event_id}/participation` — batch upsert (staff only). */
  updateParticipation(eventId: string, input: ParticipationUpdateInput): Promise<ParticipationPage>;
  /** `GET /plays/v1` — Play Archive page (unit/conflict/search filters, sort, paging). */
  listPlays(query: PlayQuery, signal?: AbortSignal): Promise<PlayPage>;
  /** `GET /plays/v1/{play_id}/versions` — every competing submission + `can_resolve`. */
  getPlayVersions(playId: string, signal?: AbortSignal): Promise<PlayVersionPage>;
  /** `PATCH /plays/v1/{play_id}` — appends a corrected version; never mutates one. */
  updatePlay(playId: string, patch: PlayPatch): Promise<PlayView>;
  /** `POST /plays/v1/{play_id}/resolve` — Primary Stat Operator / Team Admin only. */
  resolvePlayConflict(playId: string, input: ResolveConflictInput): Promise<PlayView>;
  /**
   * `PATCH /plays/v1/{play_id}/favorite` — star / unstar a play team-wide
   * (coaching staff, stat operators, Team Admin). Returns the updated play.
   */
  setPlayFavorite(playId: string, isFavorited: boolean): Promise<PlayView>;
  /**
   * `POST /plays/v1/events/{event_id}/insert` — slot a missed play in at
   * `input.position`; later plays renumber +1 server-side. The caller owns the
   * `Idempotency-Key` so a retried insert never lands twice.
   */
  insertPlay(eventId: string, input: InsertPlayInput, idempotencyKey: string): Promise<PlayView>;
  /**
   * `DELETE /plays/v1/{play_id}` — soft delete; later plays renumber −1.
   * `reason` (≥10 chars) is required once the event is closed.
   */
  deletePlay(playId: string, reason?: string): Promise<void>;
  /** `GET /playbook/v1` — units, tabs, stat definitions and the Play Entry card map. */
  getPlaybookCatalog(signal?: AbortSignal): Promise<PlaybookCatalog>;
  /** `GET /playbook/v1/library` — every enabled option on both sides, with usage. */
  getPlaybookLibrary(signal?: AbortSignal): Promise<PlaybookLibrary>;
  /** `GET /playbook/v1/stats/{category}/{side}` — one tab of the Playbook, retired rows included. */
  listStatOptions(
    category: StatCategory,
    side: StatSide,
    signal?: AbortSignal
  ): Promise<StatOptionList>;
  /** `POST /playbook/v1/stats/{category}/{side}` — add a team option. 409 on duplicate, 402 on Free. */
  createStatOption(
    category: StatCategory,
    side: StatSide,
    input: StatOptionInput
  ): Promise<StatOption>;
  /** `POST …/enabled` — bulk enable / disable. */
  setStatOptionsEnabled(
    category: StatCategory,
    side: StatSide,
    optionIds: string[],
    isEnabled: boolean
  ): Promise<{ updated: number }>;
  /** `PUT …/order` — persist a display order. */
  reorderStatOptions(
    category: StatCategory,
    side: StatSide,
    optionIds: string[]
  ): Promise<StatOptionList>;
  /** `PATCH /playbook/v1/options/{option_id}` */
  updateStatOption(optionId: string, patch: StatOptionPatch): Promise<StatOption>;
  /** `DELETE /playbook/v1/options/{option_id}` — `null` when deleted, the retirement when disabled instead. */
  removeStatOption(optionId: string): Promise<StatOptionRetired | null>;
  /**
   * `GET /stat-schema/v1` — the tenant's stat field catalog (built-ins +
   * custom), its tier capacity, the enum categories a new field can draw from
   * and `can_write`. Readable by every authenticated persona.
   */
  listStatFields(query: StatSchemaQuery, signal?: AbortSignal): Promise<StatSchemaPage>;
  /**
   * `POST /stat-schema/v1` — Team Admin only. 402 `TIER_LIMIT_EXCEEDED` when
   * the plan's field allowance is used up (FR-STATS-02).
   */
  createStatField(input: StatFieldInput): Promise<StatFieldDefinition>;
  /**
   * `PATCH /stat-schema/v1/{field_id}` — label / applies_to / enum category /
   * Hudl mapping / enabled. `stat_key` and `data_type` are immutable
   * (FR-STATS-03/04) and are rejected with 400.
   */
  updateStatField(fieldId: string, patch: StatFieldPatch): Promise<StatFieldDefinition>;
  /**
   * `DELETE /stat-schema/v1/{field_id}`. 204 when no play ever used the field;
   * 409 `FIELD_IN_USE` when one has — in which case the field has been RETIRED
   * (inactive) and every historical value is retained.
   */
  removeStatField(fieldId: string): Promise<void>;
  /**
   * `GET /users/v1/layouts` — every saved board layout the caller can see:
   * their own `user` rows plus the team's `team` rows.
   */
  listLayouts(signal?: AbortSignal): Promise<BoardLayoutPage>;
  /**
   * `PUT /users/v1/layouts/{scope}/{screen_id}` — upsert the unnamed slot.
   * Freeform preference screens only (`archive.*`, `table.*`); board screens
   * answer 409 `LAYOUT_NAME_REQUIRED` — use `saveLayoutWithName`. `team` is
   * Team Admin only.
   */
  saveLayout(
    scope: LayoutScope,
    screenId: string,
    input: BoardLayoutInput
  ): Promise<BoardLayoutRecord>;
  /** `DELETE /users/v1/layouts/{scope}/{screen_id}` — freeform preference screens only. */
  deleteLayout(scope: LayoutScope, screenId: string): Promise<void>;
  /** `GET /users/v1/layouts?screen_id=` — every saved layout for one screen + the active one. */
  listLayoutsForScreen(screenId: string, signal?: AbortSignal): Promise<BoardLayoutPage>;
  /**
   * `POST /users/v1/layouts/{scope}/{screen_id}` — save a NEW named layout.
   * 409 `LAYOUT_NAME_TAKEN` when the name exists for that scope + screen.
   */
  saveLayoutWithName(
    scope: LayoutScope,
    screenId: string,
    input: NamedLayoutInput
  ): Promise<BoardLayoutRecord>;
  /** `PATCH /users/v1/layouts/id/{layout_id}` — rename and/or save over one layout. */
  updateLayoutById(layoutId: string, patch: NamedLayoutPatch): Promise<BoardLayoutRecord>;
  /** `DELETE /users/v1/layouts/id/{layout_id}`. */
  deleteLayoutById(layoutId: string): Promise<void>;
  /** `PUT /users/v1/layouts/active/{screen_id}` — remember the selected layout (null clears). */
  setActiveLayout(screenId: string, layoutId: string | null): Promise<void>;
  /**
   * `GET /stat-schema/v1/enums/{category}` — every value of one catalog
   * category, retired rows included. The Play Entry formation Config panel
   * offers these for opt-in.
   */
  listEnumValues(category: string, signal?: AbortSignal): Promise<EnumCatalogPage>;
  /**
   * `PATCH /stat-schema/v1/enums/{category}/{value}` — Team Admin only. Retire
   * or reinstate one value; never deletes.
   */
  setEnumValueDeprecated(
    category: string,
    value: string,
    isDeprecated: boolean
  ): Promise<EnumCatalogValue>;
  /**
   * `GET /stats/v1/highlight/{event_id}?unit=…` — the Coaches Highlight feed:
   * current down, 11 key stats and 5 ranked pie charts for one side. Staff only.
   */
  getHighlight(
    eventId: string,
    side: HighlightSide,
    options?: { isForced?: boolean; signal?: AbortSignal }
  ): Promise<HighlightFeed>;
  /**
   * `GET /stats/v1/summary/{event_id}?unit=…` — Game Summary (FR-SUMMARY-*):
   * line score, drive-by-drive scoring summary and the projected O/D/ST stat
   * blocks. Readable by every authenticated persona including `fanatic`.
   */
  getGameSummary(
    eventId: string,
    options?: { unit?: StatUnitFilter; isForced?: boolean; signal?: AbortSignal }
  ): Promise<GameSummaryFeed>;
  /**
   * `GET /stats/v1/tracker/{event_id}?unit=…` — the full Stat Tracker sheet for
   * one unit (FR-STATTRACKER-01). Team members only; `fanatic` gets a 403.
   */
  getStatTracker(
    eventId: string,
    unit: StatUnit,
    options?: { isForced?: boolean; signal?: AbortSignal }
  ): Promise<StatTrackerFeed>;
  /**
   * `GET /stats/v1/season?season=YYYY&unit=…` — cross-game rollup for the
   * caller's own team (FR-STATTRACKER-02). Not live-pushed; refetch to update.
   */
  getSeasonRollup(query: SeasonRollupQuery, signal?: AbortSignal): Promise<SeasonRollupFeed>;
  /**
   * `GET /billing/v1/plans` — the PUBLIC four-tier catalog. Cacheable; carries
   * no tenant data, so the marketing pricing table uses it too.
   */
  listBillingPlans(signal?: AbortSignal): Promise<BillingPlanCatalog>;
  /**
   * `GET /billing/v1/subscriptions/{tenant_id}` — current plan, effective
   * entitlements (override already applied), trial state and usage counters.
   */
  getBillingSubscription(tenantId: string, signal?: AbortSignal): Promise<BillingSubscriptionView>;
  /**
   * `PATCH /billing/v1/subscriptions/{tenant_id}` — plan change (downgrade is
   * immediate) or `cancel_at_period_end`. Upgrades that need a card must go
   * through `createCheckoutSession`.
   */
  updateBillingSubscription(
    tenantId: string,
    patch: SubscriptionPatch
  ): Promise<BillingSubscriptionView>;
  /**
   * `POST /billing/v1/checkout-sessions` — the only card-capture door
   * (AC-BILL-003). Answers 422 for the Free plan and 503
   * `PROCESSOR_UNAVAILABLE` on invoice-billed deployments.
   */
  createCheckoutSession(input: CheckoutSessionInput): Promise<CheckoutSession>;
  /** `GET /billing/v1/invoices` — history plus the tenant's dunning state. */
  listInvoices(query: InvoiceQuery, signal?: AbortSignal): Promise<InvoicePage>;
  /** `GET /billing/v1/portal?return_path=…` — provider-hosted billing portal. */
  getBillingPortalSession(returnPath: string): Promise<BillingPortalSession>;
  /** `PATCH /tenants/v1/{tenant_id}` — team name and support-recipient email. */
  updateTenant(
    tenantId: string,
    patch: {
      name?: string;
      support_recipient_email?: string | null;
      /** Who may follow the team (AC-FANATIC-010). Audit-logged server-side. */
      fanatic_access_mode?: FanaticAccessMode;
    }
  ): Promise<TenantSummary>;

  // -- fanatic-service: the Fanatic's own surface ---------------------------
  /** `GET /fanatic/v1/grants` — "teams I follow", with counts + pagination. */
  listFanaticGrants(query: FanaticGrantQuery, signal?: AbortSignal): Promise<FanaticGrantPage>;
  /**
   * `POST /fanatic/v1/grants` — follow a team. Idempotent: 200 with the
   * existing grant when already following, 201 on a fresh follow.
   */
  followTeam(target: { tenantId?: string; teamSlug?: string }): Promise<FanaticGrant>;
  /** `DELETE /fanatic/v1/grants/{grant_id}` — self-unfollow (soft delete, reversible). */
  unfollowTeam(grantId: string): Promise<void>;
  /** `GET /fanatic/v1/teams` — public team directory with own follow state. */
  browsePublicTeams(query: PublicTeamQuery, signal?: AbortSignal): Promise<PublicTeamPage>;
  /** `GET /fanatic/v1/teams/{slug}` — one public team. */
  getPublicTeam(slug: string, signal?: AbortSignal): Promise<PublicTeam>;
  /** `GET /fanatic/v1/realm` — Fanatic IdP realm + invite TTL (deployment config). */
  getFanaticRealm(signal?: AbortSignal): Promise<FanaticRealmInfo>;
  /** `GET /fanatic/v1/invites/{token}` — name the team before accepting. */
  previewFanaticInvite(token: string, signal?: AbortSignal): Promise<FanaticInvitePreview>;
  /** `POST /fanatic/v1/invites/{token}/accept` — claim the invite. */
  acceptFanaticInvite(token: string): Promise<FanaticInviteAcceptance>;

  // -- fanatic-service: the Team Admin's surface ---------------------------
  /** `GET /tenants/v1/{id}/fanatic-grants` — the team's follower list. */
  listTeamFanaticGrants(
    tenantId: string,
    query: TeamFanaticGrantQuery,
    signal?: AbortSignal
  ): Promise<TeamFanaticGrantPage>;
  /** `POST /tenants/v1/{id}/fanatic-invites` — invite one email address. */
  sendFanaticInvite(
    tenantId: string,
    input: { email: string; fanatic_user_id?: string }
  ): Promise<SentFanaticInvite>;
  /**
   * `POST /tenants/v1/{id}/fanatic-grants/{grant_id}/revoke` — final removal.
   * `reason` is optional and free-form; it lands in the audit trail.
   */
  revokeFanaticGrant(
    tenantId: string,
    grantId: string,
    reason?: string | null
  ): Promise<RevokedFanaticGrant>;

  // -- auth-service: linked sign-in methods --------------------------------
  /** `GET /auth/v1/account/identities` — OAuth identities on this account. */
  /** `GET /auth/v1/account/mfa` — where this account stands on two-factor. */
  getMfaSettings(signal?: AbortSignal): Promise<MfaSettings>;
  /**
   * `PATCH /auth/v1/account/mfa` — turn enforcement on/off or remove the
   * authenticator. Returns a re-issued access token reflecting the change.
   */
  updateMfaSettings(patch: MfaSettingsPatch): Promise<MfaSettingsUpdate>;
  listLinkedIdentities(signal?: AbortSignal): Promise<LinkedIdentityList>;
  /** `DELETE /auth/v1/account/identities/{id}` — unlink one provider. */
  unlinkIdentity(identityId: string): Promise<void>;
  /**
   * `GET /video/v1/providers` — the capability catalog. Every authenticated
   * persona may read it; `fanatic_visible` is adapter-declared, never editable.
   */
  getVideoProviders(signal?: AbortSignal): Promise<VideoProviderCatalog>;
  /** `GET /video/v1/settings` — Team Admin view of the tenant's selection. */
  getVideoSettings(signal?: AbortSignal): Promise<VideoSettings>;
  /** `PATCH /video/v1/settings` — provider selection / Hudl fallback URL. */
  updateVideoSettings(patch: VideoSettingsPatch): Promise<VideoSettings>;
  /** `GET /video/v1/embeds` — per-event embed rows (search, filter, sort, page). */
  listEventVideoEmbeds(
    query: EventVideoEmbedQuery,
    signal?: AbortSignal
  ): Promise<EventVideoEmbedPage>;
  /** `PUT /video/v1/embeds/{event_id}` — save one event's embed URL / stream id. */
  saveEventVideoEmbed(eventId: string, input: EventVideoEmbedInput): Promise<EventVideoEmbed>;
  /** `DELETE /video/v1/embeds/{event_id}?provider=…` — clear one event's embed. */
  removeEventVideoEmbed(eventId: string, provider: 'hudl' | 'youtube'): Promise<void>;
  /**
   * `GET /video/v1/embed?event_id=…` — the embed for one event. Answers 403
   * `PROVIDER_NOT_FANATIC_VISIBLE` for a Fanatic on a coaches-only provider and
   * 404 `PROVIDER_NOT_CONFIGURED` / `EMBED_NOT_CONFIGURED` when video is off.
   */
  getVideoEmbed(eventId: string, signal?: AbortSignal): Promise<VideoEmbedInfo>;
  /** `GET /plays/v1/success-measure-config` — active rules + platform defaults. */
  getSuccessMeasureConfig(signal?: AbortSignal): Promise<SuccessMeasureConfigView>;
  /** `PUT /plays/v1/success-measure-config` — appends a new version (Team Admin). */
  updateSuccessMeasureConfig(config: SuccessMeasureConfig): Promise<SuccessMeasureConfigView>;
  /** `GET /hudl/v1/export/fields` — exportable fields + the fixed Hudl columns. 402 below Varsity. */
  getHudlExportFields(signal?: AbortSignal): Promise<HudlExportFieldCatalog>;
  /** `GET /hudl/v1/export/templates` — the team's saved custom export layouts. */
  listHudlExportTemplates(signal?: AbortSignal): Promise<HudlExportTemplatePage>;
  /** `POST /hudl/v1/export/templates` — 409 `DUPLICATE_PRESET_NAME`. */
  createHudlExportTemplate(input: HudlExportTemplateInput): Promise<HudlExportTemplate>;
  /** `PATCH /hudl/v1/export/templates/{template_id}` */
  updateHudlExportTemplate(
    templateId: string,
    patch: HudlExportTemplatePatch
  ): Promise<HudlExportTemplate>;
  /** `DELETE /hudl/v1/export/templates/{template_id}` */
  removeHudlExportTemplate(templateId: string): Promise<void>;
  /**
   * `GET /hudl/v1/export/file` — the file itself (xlsx or csv), fetched with the
   * bearer token; the name comes from `Content-Disposition`.
   */
  downloadHudlExport(query: HudlExportFileQuery): Promise<DownloadedFile>;
  /**
   * `POST /exports/v1/table` — site-wide export: the page's own rows and
   * columns in, an xlsx / csv file out (413 `EXPORT_TOO_LARGE` past the limits).
   */
  downloadTableExport(input: TableExportRequest): Promise<DownloadedFile>;
  /** `GET /exports/v1/templates` — every saved layout of the team (all pages). */
  listExportTemplates(signal?: AbortSignal): Promise<{ items: ExportTemplate[] }>;
  /** `POST /exports/v1/templates` — 409 `DUPLICATE_EXPORT_TEMPLATE_NAME` on a taken name. */
  createExportTemplate(input: ExportTemplateInput): Promise<ExportTemplate>;
  /** `PATCH /exports/v1/templates/{template_id}` — rename / re-column / file type. */
  updateExportTemplate(templateId: string, patch: ExportTemplatePatch): Promise<ExportTemplate>;
  /** `DELETE /exports/v1/templates/{template_id}`. */
  deleteExportTemplate(templateId: string): Promise<void>;
  /** `POST /hudl/v1/import/preview` — multipart upload, returns what the file holds. */
  previewHudlImport(file: File): Promise<HudlImportPreview>;
  /** `POST /hudl/v1/import/{upload_id}/reconcile` — line the file up with a game's plays. */
  reconcileHudlImport(uploadId: string, input: HudlReconcileInput): Promise<HudlReconcileResult>;
  /** `POST /hudl/v1/import/{upload_id}/commit` — 409 `RECONCILE_STALE` if the game changed. */
  commitHudlImport(uploadId: string, input: HudlCommitInput): Promise<HudlCommitResult>;
  /** `GET /hudl/v1/imports?limit=` — recent imports. */
  listHudlImports(limit?: number, signal?: AbortSignal): Promise<HudlImportRecordPage>;
  /**
   * `GET /help/v1/faqs` — published entries only. Persona defaults to the
   * caller's; `all_roles: true` drops the persona filter entirely.
   */
  listFaqs(query: FaqQuery, signal?: AbortSignal): Promise<FaqPage>;
  /**
   * `GET /help/v1/tooltips` — resolves tenant > platform > shipped default.
   * Omit `keys` to hydrate the whole registry in one call.
   */
  getTooltips(keys?: readonly string[], signal?: AbortSignal): Promise<TooltipResolution>;
  /**
   * `GET /support/v1/recipient-status` — FR-HELP-07. The Contact Support form
   * calls this on mount and again immediately before submit, so a recipient a
   * Platform Admin configured seconds ago is picked up without a reload.
   */
  getSupportRecipientStatus(signal?: AbortSignal): Promise<SupportRecipientStatus>;
  /**
   * `POST /support/v1/requests` — FR-HELP-06. Sent as multipart when an image
   * is attached, otherwise as JSON.
   */
  submitSupportRequest(input: SupportRequestInput): Promise<SupportRequestCreated>;
  /**
   * Full-page URL that starts a brokered OAuth2/OIDC login. `state` is opaque
   * client state echoed back in the callback fragment — the web client puts the
   * team slug there so the return leg knows which portal was being entered.
   */
  oauthAuthorizeUrl(
    provider: string,
    tenantId: string,
    redirectUri: string,
    state?: string
  ): string;
}

/**
 * File name from a `Content-Disposition` header (`filename*=UTF-8''…` wins
 * over `filename="…"`), or `null`.
 */
export function fileNameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  if (!plain) return null;
  const name = (plain[2] ?? plain[1]).trim();
  return name === '' ? null : name;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const newKey =
    options.newIdempotencyKey ??
    (() => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}`));

  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (MUTATION_METHODS.has(method)) headers['Idempotency-Key'] = opts.idempotencyKey ?? newKey();
    if (opts.isPublic !== true) {
      const auth = options.getAuthContext();
      if (auth.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;
      if (auth.tenantId) headers['X-Tenant-ID'] = auth.tenantId;
    }

    let response: Response;
    try {
      response = await doFetch(buildUrl(options.baseUrl, path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
        credentials: 'same-origin',
      });
    } catch {
      throw toNetworkError();
    }

    if (response.status === 204) return undefined as T;

    const raw = await response.text();
    const parsed: unknown = raw === '' ? null : safeJsonParse(raw);
    if (!response.ok) {
      const error = toApiError(
        response.status,
        parsed,
        `The request failed (HTTP ${response.status}). Please try again.`
      );
      if (response.status === 401 && opts.isPublic !== true) {
        // A credential check (MFA code, current password) that fails with a
        // domain code is the user's mistake, not a dead session — keep them
        // signed in so they can retry. Anything else (or a credential code
        // even on a credential check) means the token is unusable.
        if (opts.isCredentialCheck !== true || isSessionError(error)) options.onUnauthorized?.();
      }
      if (
        error.status === TIER_LIMIT_STATUS &&
        error.code === TIER_LIMIT_ERROR_CODE &&
        opts.handlesTierLimit !== true
      ) {
        options.onTierLimit?.(error);
      }
      throw error;
    }
    return parsed as T;
  }

  /** Auth headers shared by the JSON, upload and download paths. */
  function authHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const auth = options.getAuthContext();
    if (auth.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;
    if (auth.tenantId) headers['X-Tenant-ID'] = auth.tenantId;
    return headers;
  }

  /**
   * Multipart POST. `Content-Type` is deliberately unset so the browser writes
   * the boundary; everything else (auth, idempotency, RFC 7807 errors, the tier
   * -limit hook) behaves exactly as `request`.
   */
  async function upload<T>(path: string, form: FormData, idempotencyKey?: string): Promise<T> {
    let response: Response;
    try {
      response = await doFetch(buildUrl(options.baseUrl, path, undefined), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...authHeaders(),
          'Idempotency-Key': idempotencyKey ?? newKey(),
        },
        body: form,
        credentials: 'same-origin',
      });
    } catch {
      throw toNetworkError();
    }
    if (response.status === 401) options.onUnauthorized?.();
    const raw = await response.text();
    const parsed: unknown = raw === '' ? null : safeJsonParse(raw);
    if (!response.ok) {
      const error = toApiError(
        response.status,
        parsed,
        `The upload failed (HTTP ${response.status}). Please try again.`
      );
      if (error.status === TIER_LIMIT_STATUS && error.code === TIER_LIMIT_ERROR_CODE) {
        options.onTierLimit?.(error);
      }
      throw error;
    }
    return parsed as T;
  }

  /** Authenticated file download — the caller turns the blob into a save link. */
  async function download(
    path: string,
    query?: RequestOptions['query'],
    accept = 'text/csv'
  ): Promise<Blob> {
    let response: Response;
    try {
      response = await doFetch(buildUrl(options.baseUrl, path, query), {
        method: 'GET',
        headers: { Accept: accept, ...authHeaders() },
        credentials: 'same-origin',
      });
    } catch {
      throw toNetworkError();
    }
    if (response.status === 401) options.onUnauthorized?.();
    if (!response.ok) {
      const raw = await response.text();
      throw toApiError(
        response.status,
        raw === '' ? null : safeJsonParse(raw),
        `The download failed (HTTP ${response.status}). Please try again.`
      );
    }
    return response.blob();
  }

  /**
   * Like {@link download}, plus the file name from `Content-Disposition`.
   * With `body` it POSTs JSON (the site-wide table export sends its rows).
   */
  async function downloadNamed(
    path: string,
    query: RequestOptions['query'],
    accept: string,
    body?: unknown
  ): Promise<DownloadedFile> {
    let response: Response;
    try {
      response = await doFetch(buildUrl(options.baseUrl, path, query), {
        method: body === undefined ? 'GET' : 'POST',
        headers:
          body === undefined
            ? { Accept: accept, ...authHeaders() }
            : { Accept: accept, 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'same-origin',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw toNetworkError();
    }
    if (response.status === 401) options.onUnauthorized?.();
    if (!response.ok) {
      const raw = await response.text();
      const error = toApiError(
        response.status,
        raw === '' ? null : safeJsonParse(raw),
        `The download failed (HTTP ${response.status}). Please try again.`
      );
      if (error.status === TIER_LIMIT_STATUS && error.code === TIER_LIMIT_ERROR_CODE) {
        options.onTierLimit?.(error);
      }
      throw error;
    }
    const blob = await response.blob();
    return {
      blob,
      file_name: fileNameFromDisposition(response.headers.get('content-disposition')),
    };
  }

  return {
    request,
    downloadExport: (path, query) =>
      download(path, query, 'text/csv, application/x-ndjson, application/json'),
    getSignupOptions: (signal) =>
      request<SignupOptions>(API_PATHS.signupOptions, { isPublic: true, signal }),
    checkSlugAvailability: (slug, signal) =>
      request<SlugAvailability>(API_PATHS.slugAvailability, {
        isPublic: true,
        query: { slug },
        signal,
      }),
    lookupTeam: (slug, signal) =>
      request<TeamLookup>(API_PATHS.teamLookup, { isPublic: true, query: { slug }, signal }),
    getFanSignInOptions: (signal) =>
      request<FanSignInOptions>(API_PATHS.fanSignInOptions, { isPublic: true, signal }),
    signUpFanatic: (input) =>
      request<TokenResponse>(API_PATHS.fanaticSignup, {
        method: 'POST',
        isPublic: true,
        body: {
          email: input.email,
          password: input.password,
          // Omitted rather than sent empty: the server treats a blank string
          // as absent anyway, and a fan should never be forced to give a name.
          ...(input.first_name === undefined ? {} : { first_name: input.first_name }),
          ...(input.last_name === undefined ? {} : { last_name: input.last_name }),
        },
      }),
    createTeam: (input) =>
      request<SignupResult>(API_PATHS.signup, { method: 'POST', body: input, isPublic: true }),
    login: (input) =>
      request<TokenResponse>(API_PATHS.token, {
        method: 'POST',
        isPublic: true,
        body: {
          grant_type: 'password',
          tenant_id: input.tenantId,
          username: input.email,
          password: input.password,
        },
      }),
    refresh: (refreshToken) =>
      request<TokenResponse>(API_PATHS.tokenRefresh, {
        method: 'POST',
        isPublic: true,
        body: { refresh_token: refreshToken },
      }),
    revokeRefreshToken: (refreshToken) =>
      request<void>(API_PATHS.tokenRevoke, {
        method: 'POST',
        body: { refresh_token: refreshToken },
      }),
    enrollMfa: () =>
      request<MfaEnrollment>(API_PATHS.mfaEnroll, { method: 'POST', body: { method: 'totp' } }),
    verifyMfa: (code) => {
      // The MFA step runs on the PARTIAL token the password step issued
      // (`mfa_verified: false`; the route is MFA-exempt at the gateway). It is
      // read from the session context like every authenticated call — but if
      // it is already gone (signed out in another tab, storage cleared) there
      // is nothing to verify against, and the gateway's answer would be the
      // engineer-facing "This endpoint requires a bearer token.". Fail here
      // with the message the user should actually see.
      if (!options.getAuthContext().accessToken) {
        return Promise.reject(
          new ApiError({ status: 401, code: 'UNAUTHORIZED', message: SESSION_EXPIRED_MESSAGE })
        );
      }
      return request<MfaVerification>(API_PATHS.mfaVerify, {
        method: 'POST',
        body: { code },
        // 401 MFA_INVALID_CODE = wrong code, not a dead session.
        isCredentialCheck: true,
      });
    },
    requestPasswordReset: (input) =>
      request<PasswordResetRequested>(API_PATHS.passwordResetRequest, {
        method: 'POST',
        isPublic: true,
        body:
          input.tenantId === undefined
            ? { email: input.email }
            : { tenant_id: input.tenantId, email: input.email },
      }),
    changeAccountEmail: (input) =>
      request<AccountEmailChanged>(API_PATHS.account, {
        method: 'PATCH',
        body:
          input.currentPassword === undefined
            ? { email: input.email }
            : { email: input.email, current_password: input.currentPassword },
      }),
    confirmPasswordReset: async (input) => {
      await request<void>(API_PATHS.passwordResetConfirm, {
        method: 'POST',
        isPublic: true,
        body: { token: input.token, new_password: input.newPassword },
      });
    },
    listSessions: (query, signal) =>
      request<SessionPage>(API_PATHS.sessions, { query: { ...query }, signal }),
    revokeSession: async (sessionId) => {
      await request<void>(`${API_PATHS.sessions}/${sessionId}`, { method: 'DELETE' });
    },
    getTenant: (tenantId) => request<TenantSummary>(`${API_PATHS.tenant}/${tenantId}`),
    listTeamUsers: (query, signal) =>
      request<TeamUserPage>(API_PATHS.users, {
        // `undefined` entries are dropped by buildUrl, so an unset filter never
        // reaches the server as an empty string.
        query: { ...query },
        signal,
      }),
    createTeamUser: (input) =>
      request<CreatedTeamUser>(API_PATHS.users, { method: 'POST', body: input }),
    updateTeamUser: (userId, patch) =>
      request<TeamUser>(`${API_PATHS.users}/${userId}`, { method: 'PATCH', body: patch }),
    removeTeamUser: async (userId) => {
      await request<void>(`${API_PATHS.users}/${userId}`, { method: 'DELETE' });
    },
    listPlayers: (query, signal) =>
      request<PlayerPage>(API_PATHS.players, { query: { ...query }, signal }),
    createPlayer: (input) => request<Player>(API_PATHS.players, { method: 'POST', body: input }),
    updatePlayer: (playerId, patch) =>
      request<Player>(`${API_PATHS.players}/${playerId}`, { method: 'PATCH', body: patch }),
    removePlayer: async (playerId) => {
      await request<void>(`${API_PATHS.players}/${playerId}`, { method: 'DELETE' });
    },
    getPlaybookCatalog: (signal) => request<PlaybookCatalog>(API_PATHS.playbook, { signal }),
    getPlaybookLibrary: (signal) =>
      request<PlaybookLibrary>(`${API_PATHS.playbook}/library`, { signal }),
    listStatOptions: (category, side, signal) =>
      request<StatOptionList>(`${API_PATHS.playbook}/stats/${category}/${side}`, { signal }),
    createStatOption: (category, side, input) =>
      request<StatOption>(`${API_PATHS.playbook}/stats/${category}/${side}`, {
        method: 'POST',
        body: input,
      }),
    setStatOptionsEnabled: (category, side, optionIds, isEnabled) =>
      request<{ updated: number }>(`${API_PATHS.playbook}/stats/${category}/${side}/enabled`, {
        method: 'POST',
        body: { option_ids: optionIds, is_enabled: isEnabled },
      }),
    reorderStatOptions: (category, side, optionIds) =>
      request<StatOptionList>(`${API_PATHS.playbook}/stats/${category}/${side}/order`, {
        method: 'PUT',
        body: { option_ids: optionIds },
      }),
    updateStatOption: (optionId, patch) =>
      request<StatOption>(`${API_PATHS.playbook}/options/${optionId}`, {
        method: 'PATCH',
        body: patch,
      }),
    removeStatOption: async (optionId) => {
      const result = await request<StatOptionRetired | undefined>(
        `${API_PATHS.playbook}/options/${optionId}`,
        { method: 'DELETE' }
      );
      return result === undefined || result === null ? null : result;
    },
    listStatFields: (query, signal) =>
      request<StatSchemaPage>(API_PATHS.statSchema, { query: { ...query }, signal }),
    createStatField: (input) =>
      request<StatFieldDefinition>(API_PATHS.statSchema, { method: 'POST', body: input }),
    updateStatField: (fieldId, patch) =>
      request<StatFieldDefinition>(`${API_PATHS.statSchema}/${fieldId}`, {
        method: 'PATCH',
        body: patch,
      }),
    removeStatField: async (fieldId) => {
      await request<void>(`${API_PATHS.statSchema}/${fieldId}`, { method: 'DELETE' });
    },
    listLayouts: (signal) => request<BoardLayoutPage>(`${API_PATHS.users}/layouts`, { signal }),
    saveLayout: (scope, screenId, input) =>
      request<BoardLayoutRecord>(
        `${API_PATHS.users}/layouts/${scope}/${encodeURIComponent(screenId)}`,
        { method: 'PUT', body: input }
      ),
    deleteLayout: async (scope, screenId) => {
      await request<void>(`${API_PATHS.users}/layouts/${scope}/${encodeURIComponent(screenId)}`, {
        method: 'DELETE',
      });
    },
    listLayoutsForScreen: (screenId, signal) =>
      request<BoardLayoutPage>(
        `${API_PATHS.users}/layouts?screen_id=${encodeURIComponent(screenId)}`,
        { signal }
      ),
    saveLayoutWithName: (scope, screenId, input) =>
      request<BoardLayoutRecord>(
        `${API_PATHS.users}/layouts/${scope}/${encodeURIComponent(screenId)}`,
        { method: 'POST', body: input }
      ),
    updateLayoutById: (layoutId, patch) =>
      request<BoardLayoutRecord>(`${API_PATHS.users}/layouts/id/${encodeURIComponent(layoutId)}`, {
        method: 'PATCH',
        body: patch,
      }),
    deleteLayoutById: async (layoutId) => {
      await request<void>(`${API_PATHS.users}/layouts/id/${encodeURIComponent(layoutId)}`, {
        method: 'DELETE',
      });
    },
    setActiveLayout: async (screenId, layoutId) => {
      await request<unknown>(`${API_PATHS.users}/layouts/active/${encodeURIComponent(screenId)}`, {
        method: 'PUT',
        body: { layout_id: layoutId },
      });
    },
    listEnumValues: (category, signal) =>
      request<EnumCatalogPage>(`${API_PATHS.statSchema}/enums/${encodeURIComponent(category)}`, {
        signal,
      }),
    setEnumValueDeprecated: (category, value, isDeprecated) =>
      request<EnumCatalogValue>(
        `${API_PATHS.statSchema}/enums/${encodeURIComponent(category)}/${encodeURIComponent(value)}`,
        { method: 'PATCH', body: { is_deprecated: isDeprecated } }
      ),
    listCohortValues: (query, signal) =>
      request<CohortValuePage>(API_PATHS.cohortValues, { query: { ...query }, signal }),
    createCohortValue: (input) =>
      request<CohortValue>(API_PATHS.cohortValues, { method: 'POST', body: input }),
    listEvents: (query, signal) =>
      request<EventPage>(API_PATHS.events, { query: { ...query }, signal }),
    getEvent: (eventId, signal) => request<TeamEvent>(`${API_PATHS.events}/${eventId}`, { signal }),
    createEvent: (input) => request<TeamEvent>(API_PATHS.events, { method: 'POST', body: input }),
    updateEvent: (eventId, patch) =>
      request<TeamEvent>(`${API_PATHS.events}/${eventId}`, { method: 'PATCH', body: patch }),
    closeEvent: (eventId, input) =>
      request<TeamEvent>(`${API_PATHS.events}/${eventId}/close`, { method: 'POST', body: input }),
    reopenEvent: (eventId, input) =>
      request<TeamEvent>(`${API_PATHS.events}/${eventId}/reopen`, { method: 'POST', body: input }),
    deleteEvent: async (eventId) => {
      await request<void>(`${API_PATHS.events}/${eventId}`, { method: 'DELETE' });
    },
    listParticipation: (eventId, signal) =>
      request<ParticipationPage>(`${API_PATHS.events}/${eventId}/participation`, { signal }),
    updateParticipation: (eventId, input) =>
      request<ParticipationPage>(`${API_PATHS.events}/${eventId}/participation`, {
        method: 'PUT',
        body: input,
      }),
    listPlays: (query, signal) =>
      request<PlayPage>(API_PATHS.plays, { query: { ...query }, signal }),
    getPlayVersions: (playId, signal) =>
      request<PlayVersionPage>(`${API_PATHS.plays}/${playId}/versions`, { signal }),
    updatePlay: (playId, patch) =>
      request<PlayView>(`${API_PATHS.plays}/${playId}`, { method: 'PATCH', body: patch }),
    resolvePlayConflict: (playId, input) =>
      request<PlayView>(`${API_PATHS.plays}/${playId}/resolve`, { method: 'POST', body: input }),
    setPlayFavorite: (playId, isFavorited) =>
      request<PlayView>(`${API_PATHS.plays}/${encodeURIComponent(playId)}/favorite`, {
        method: 'PATCH',
        body: { is_favorited: isFavorited },
      }),
    insertPlay: (eventId, input, idempotencyKey) =>
      request<PlayView>(`${API_PATHS.plays}/events/${encodeURIComponent(eventId)}/insert`, {
        method: 'POST',
        body: input,
        idempotencyKey,
      }),
    deletePlay: async (playId, reason) => {
      await request<void>(`${API_PATHS.plays}/${playId}`, {
        method: 'DELETE',
        body: reason === undefined ? undefined : { reason },
      });
    },
    getHighlight: (eventId, side, opts) =>
      request<HighlightFeed>(`${API_PATHS.stats}/highlight/${eventId}`, {
        query: { unit: side, refresh: opts?.isForced === true ? 'true' : undefined },
        signal: opts?.signal,
      }),
    getGameSummary: (eventId, opts) =>
      request<GameSummaryFeed>(`${API_PATHS.stats}/summary/${eventId}`, {
        query: {
          unit: opts?.unit ?? 'all',
          refresh: opts?.isForced === true ? 'true' : undefined,
        },
        signal: opts?.signal,
      }),
    getStatTracker: (eventId, unit, opts) =>
      request<StatTrackerFeed>(`${API_PATHS.stats}/tracker/${eventId}`, {
        query: { unit, refresh: opts?.isForced === true ? 'true' : undefined },
        signal: opts?.signal,
      }),
    getSeasonRollup: (query, signal) =>
      request<SeasonRollupFeed>(`${API_PATHS.stats}/season`, {
        query: { season: query.season, unit: query.unit ?? 'all', team_id: query.team_id },
        signal,
      }),
    listBillingPlans: (signal) =>
      request<BillingPlanCatalog>(API_PATHS.billingPlans, { isPublic: true, signal }),
    getBillingSubscription: (tenantId, signal) =>
      request<BillingSubscriptionView>(`${API_PATHS.billingSubscriptions}/${tenantId}`, { signal }),
    updateBillingSubscription: (tenantId, patch) =>
      request<BillingSubscriptionView>(`${API_PATHS.billingSubscriptions}/${tenantId}`, {
        method: 'PATCH',
        body: patch,
      }),
    createCheckoutSession: (input) =>
      request<CheckoutSession>(API_PATHS.billingCheckoutSessions, { method: 'POST', body: input }),
    listInvoices: (query, signal) =>
      request<InvoicePage>(API_PATHS.billingInvoices, { query: { ...query }, signal }),
    getBillingPortalSession: (returnPath) =>
      request<BillingPortalSession>(API_PATHS.billingPortal, {
        query: { return_path: returnPath },
      }),
    updateTenant: (tenantId, patch) =>
      request<TenantSummary>(`${API_PATHS.tenant}/${tenantId}`, { method: 'PATCH', body: patch }),

    listFanaticGrants: (query, signal) =>
      request<FanaticGrantPage>(API_PATHS.fanaticGrants, { query: { ...query }, signal }),
    followTeam: (target) =>
      request<FanaticGrant>(API_PATHS.fanaticGrants, {
        method: 'POST',
        body: target.tenantId ? { tenant_id: target.tenantId } : { team_slug: target.teamSlug },
      }),
    unfollowTeam: (grantId) =>
      request<void>(`${API_PATHS.fanaticGrants}/${grantId}`, { method: 'DELETE' }),
    browsePublicTeams: (query, signal) =>
      request<PublicTeamPage>(API_PATHS.fanaticTeams, { query: { ...query }, signal }),
    getPublicTeam: (slug, signal) =>
      request<PublicTeam>(`${API_PATHS.fanaticTeams}/${encodeURIComponent(slug)}`, { signal }),
    getFanaticRealm: (signal) => request<FanaticRealmInfo>(API_PATHS.fanaticRealm, { signal }),
    previewFanaticInvite: (token, signal) =>
      request<FanaticInvitePreview>(`${API_PATHS.fanaticInvites}/${encodeURIComponent(token)}`, {
        signal,
      }),
    acceptFanaticInvite: (token) =>
      request<FanaticInviteAcceptance>(
        `${API_PATHS.fanaticInvites}/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: {} }
      ),

    listTeamFanaticGrants: (tenantId, query, signal) =>
      request<TeamFanaticGrantPage>(`${API_PATHS.tenant}/${tenantId}/fanatic-grants`, {
        query: { ...query },
        signal,
      }),
    sendFanaticInvite: (tenantId, input) =>
      request<SentFanaticInvite>(`${API_PATHS.tenant}/${tenantId}/fanatic-invites`, {
        method: 'POST',
        body: input,
      }),
    revokeFanaticGrant: (tenantId, grantId, reason) =>
      request<RevokedFanaticGrant>(
        `${API_PATHS.tenant}/${tenantId}/fanatic-grants/${grantId}/revoke`,
        { method: 'POST', body: reason ? { reason } : {} }
      ),

    getMfaSettings: (signal) => request<MfaSettings>(API_PATHS.accountMfa, { signal }),
    updateMfaSettings: (patch) =>
      request<MfaSettingsUpdate>(API_PATHS.accountMfa, {
        method: 'PATCH',
        body: patch,
        // 401 INVALID_CREDENTIALS = wrong current password, not a dead session.
        isCredentialCheck: true,
      }),
    listLinkedIdentities: (signal) =>
      request<LinkedIdentityList>(API_PATHS.accountIdentities, { signal }),
    unlinkIdentity: (identityId) =>
      request<void>(`${API_PATHS.accountIdentities}/${identityId}`, { method: 'DELETE' }),

    getVideoProviders: (signal) =>
      request<VideoProviderCatalog>(API_PATHS.videoProviders, { signal }),
    // The Video tab and the Live Video pages draw their own inline plan-gate
    // card on 402, so these reads opt out of the app-wide tier-limit modal.
    getVideoSettings: (signal) =>
      request<VideoSettings>(API_PATHS.videoSettings, { signal, handlesTierLimit: true }),
    updateVideoSettings: (patch) =>
      request<VideoSettings>(API_PATHS.videoSettings, { method: 'PATCH', body: patch }),
    listEventVideoEmbeds: (query, signal) =>
      request<EventVideoEmbedPage>(API_PATHS.videoEmbeds, {
        query: { ...query },
        signal,
        handlesTierLimit: true,
      }),
    saveEventVideoEmbed: (eventId, input) =>
      request<EventVideoEmbed>(`${API_PATHS.videoEmbeds}/${eventId}`, {
        method: 'PUT',
        body: input,
      }),
    removeEventVideoEmbed: async (eventId, provider) => {
      await request<void>(`${API_PATHS.videoEmbeds}/${eventId}`, {
        method: 'DELETE',
        query: { provider },
      });
    },
    getVideoEmbed: (eventId, signal) =>
      request<VideoEmbedInfo>(API_PATHS.videoEmbed, {
        query: { event_id: eventId },
        signal,
        handlesTierLimit: true,
      }),
    getSuccessMeasureConfig: (signal) =>
      request<SuccessMeasureConfigView>(API_PATHS.successMeasureConfig, { signal }),
    updateSuccessMeasureConfig: (config) =>
      request<SuccessMeasureConfigView>(API_PATHS.successMeasureConfig, {
        method: 'PUT',
        body: config,
      }),
    getHudlExportFields: (signal) =>
      request<HudlExportFieldCatalog>(API_PATHS.hudlExportFields, { signal }),
    listHudlExportTemplates: (signal) =>
      request<HudlExportTemplatePage>(API_PATHS.hudlExportTemplates, { signal }),
    createHudlExportTemplate: (input) =>
      request<HudlExportTemplate>(API_PATHS.hudlExportTemplates, { method: 'POST', body: input }),
    updateHudlExportTemplate: (templateId, patch) =>
      request<HudlExportTemplate>(`${API_PATHS.hudlExportTemplates}/${templateId}`, {
        method: 'PATCH',
        body: patch,
      }),
    removeHudlExportTemplate: async (templateId) => {
      await request<void>(`${API_PATHS.hudlExportTemplates}/${templateId}`, { method: 'DELETE' });
    },
    downloadHudlExport: (query) =>
      downloadNamed(
        API_PATHS.hudlExportFile,
        {
          event_id: query.event_id,
          format: query.format,
          template_id: query.template_id,
          columns: query.columns === undefined ? undefined : JSON.stringify(query.columns),
          file_type: query.file_type,
        },
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, application/json'
      ),
    downloadTableExport: (input) =>
      downloadNamed(
        API_PATHS.tableExport,
        undefined,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, application/json',
        input
      ),
    listExportTemplates: (signal) =>
      request<{ items: ExportTemplate[] }>(API_PATHS.exportTemplates, { signal }),
    createExportTemplate: (input) =>
      request<ExportTemplate>(API_PATHS.exportTemplates, { method: 'POST', body: input }),
    updateExportTemplate: (templateId, patch) =>
      request<ExportTemplate>(`${API_PATHS.exportTemplates}/${encodeURIComponent(templateId)}`, {
        method: 'PATCH',
        body: patch,
      }),
    deleteExportTemplate: async (templateId) => {
      await request<void>(`${API_PATHS.exportTemplates}/${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
      });
    },
    previewHudlImport: (file) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return upload<HudlImportPreview>(API_PATHS.hudlImportPreview, form);
    },
    reconcileHudlImport: (uploadId, input) =>
      request<HudlReconcileResult>(`${API_PATHS.hudlImport}/${uploadId}/reconcile`, {
        method: 'POST',
        body: input,
      }),
    commitHudlImport: (uploadId, input) =>
      request<HudlCommitResult>(`${API_PATHS.hudlImport}/${uploadId}/commit`, {
        method: 'POST',
        body: input,
      }),
    listHudlImports: (limit, signal) =>
      request<HudlImportRecordPage>(API_PATHS.hudlImports, {
        query: { limit: limit === undefined ? undefined : String(limit) },
        signal,
      }),
    listFaqs: (query, signal) =>
      request<FaqPage>(API_PATHS.faqs, {
        query: {
          persona: query.all_roles === true ? undefined : query.persona,
          all_roles: query.all_roles === true ? 'true' : undefined,
          q: query.q === undefined || query.q.trim() === '' ? undefined : query.q.trim(),
          limit: query.limit === undefined ? undefined : String(query.limit),
          offset: query.offset === undefined ? undefined : String(query.offset),
        },
        signal,
      }),
    getTooltips: (keys, signal) =>
      request<TooltipResolution>(API_PATHS.tooltips, {
        query: { keys: keys === undefined || keys.length === 0 ? undefined : keys.join(',') },
        signal,
      }),
    getSupportRecipientStatus: (signal) =>
      request<SupportRecipientStatus>(API_PATHS.supportRecipientStatus, { signal }),
    submitSupportRequest: (input) => {
      const { attachment, ...fields } = input;
      if (!attachment) {
        return request<SupportRequestCreated>(API_PATHS.supportRequests, {
          method: 'POST',
          body: fields,
        });
      }
      const form = new FormData();
      form.append('category', fields.category);
      form.append('subject', fields.subject);
      form.append('description', fields.description);
      if (fields.diagnostic_bundle !== undefined) {
        form.append('diagnostic_bundle', JSON.stringify(fields.diagnostic_bundle));
      }
      form.append('attachment', attachment, attachment.name);
      return upload<SupportRequestCreated>(API_PATHS.supportRequests, form);
    },
    oauthAuthorizeUrl: (provider, tenantId, redirectUri, state) =>
      buildUrl(options.baseUrl, `${API_PATHS.oauthAuthorize}/${provider}/authorize`, {
        tenant_id: tenantId,
        redirect_uri: redirectUri,
        state,
      }),
  };
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Base URL for API calls. Relative by default: same origin as the app. */
export function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;
  return typeof configured === 'string' && configured !== ''
    ? configured.replace(/\/$/, '')
    : '/api';
}
